import { useState, useCallback, useEffect } from "react";
import messages from "messages";
// components
import GridItem from "./components/grid-item";
import DropZone from "../../../../../upload-zone/components/drop-zone";
// styles
import styles from "./styles.module.scss";
// types
import type { AttachmentsViewProps } from "../..";
import type {
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
// hooks
import useRenameEntry from "../../hooks/useRenameEntry";

const GridView = ({
  selected = [],
  dataInView: { filesInView, foldersInView },
  setFolderPaths,
  registerSelectableRef,
  deleteEntry,
  hideActionsMenu,
  storageDrive,
  onDrop,
  batchEntryOps,
  onError,
  onDownload,
  isSelectedDragging,
  setIsSelectedDragging,
  onMoveEnd,
  setSelected,
  analyticsPageName,
}: AttachmentsViewProps) => {
  const [draggingOver, setDraggingOver] = useState(false);
  const renameEntryProps = useRenameEntry({
    batchEntryOps,
    setSelected,
    onError,
  });

  const onDragFilesEnd = useCallback(
    async (item: OrchidDriveFile | OrchidDriveDirectory) => {
      if (!batchEntryOps || !isSelectedDragging) {
        return;
      }

      if ("directoryId" in item) {
        if (selected.some((el) => el.guid === item.directoryId)) {
          return;
        }

        try {
          await batchEntryOps({
            entryMoves: selected.map((el) => ({
              name: el.name,
              entryId: el.guid,
              parentDirectoryId: item.directoryId,
            })),
            entryDeletes: [],
          });

          return (
            onMoveEnd &&
            onMoveEnd({
              selected,
              targetFolder: item,
              success: true,
            })
          );
        } catch (error) {
          console.error(error);
          return (
            onMoveEnd &&
            onMoveEnd({
              selected,
              targetFolder: item,
              success: false,
            })
          );
        }
      }
    },
    [batchEntryOps, isSelectedDragging, onMoveEnd, selected],
  );

  // need for safari since :hover will not work
  const [hoverId, setHoverId] = useState("");

  useEffect(() => {
    if (!isSelectedDragging) {
      setHoverId("");
    }
  }, [isSelectedDragging]);

  return (
    <div
      className={styles["grid-view"]}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!renameEntryProps.showRename) {
          setDraggingOver(true);
        }
      }}
    >
      {draggingOver && (
        <DropZone
          className={styles["grid-view__drop-zone"]}
          storageDrive={storageDrive}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggingOver(false);
            return onDrop(e);
          }}
          // !! need this onDragOver to make onDrop work
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggingOver(false);
          }}
        />
      )}
      {foldersInView.length > 0 && (
        <div className={styles["grid-view__section"]}>
          <div className={styles["grid-view__header"]}>{messages.folders}</div>
          <div className={styles["grid-view__body"]}>
            {foldersInView.map((el) => (
              <GridItem
                entry={el}
                entryId={el.directoryId}
                checked={selected.some((each) => each.guid === el.directoryId)}
                key={el.directoryId}
                type={"folder"}
                onClickFolder={() => setFolderPaths((v) => [...v, el])}
                ref={(element) =>
                  registerSelectableRef &&
                  registerSelectableRef(el.directoryId, {
                    element,
                    value: { ...el, type: "folder", guid: el.directoryId },
                  })
                }
                deleteEntry={deleteEntry}
                batchEntryOps={batchEntryOps}
                onError={onError}
                onDownload={onDownload}
                onDragFilesStart={() => {
                  setIsSelectedDragging(true);
                }}
                onDragFilesEnd={() => onDragFilesEnd(el)}
                isSelectedDragging={isSelectedDragging}
                onMouseOver={(id) => setHoverId(() => id)}
                onMouseOut={() => setHoverId(() => "")}
                hoverId={hoverId}
                analyticsPageName={analyticsPageName}
                {...renameEntryProps}
              />
            ))}
          </div>
        </div>
      )}
      {filesInView.length > 0 && (
        <div className={styles["grid-view__section"]}>
          <div className={styles["grid-view__header"]}>{messages.files}</div>
          <div className={styles["grid-view__body"]}>
            {filesInView.map((el) => (
              <GridItem
                entry={el}
                entryId={el.fileDataId}
                checked={selected.some((each) => each.guid === el.fileDataId)}
                key={el.fileDataId}
                type={el.mimeType}
                downloadHref={`/drive/download/${el.fileDataId}`}
                viewHref={`/drive/view/${el.fileDataId}`}
                ref={(element) =>
                  registerSelectableRef &&
                  registerSelectableRef(el.fileDataId, {
                    element,
                    value: { ...el, type: el.mimeType, guid: el.fileDataId },
                  })
                }
                deleteEntry={deleteEntry}
                hideActionsMenu={hideActionsMenu}
                batchEntryOps={batchEntryOps}
                onError={onError}
                onDownload={onDownload}
                onDragFilesStart={() => {
                  setIsSelectedDragging(true);
                }}
                onDragFilesEnd={() => onDragFilesEnd(el)}
                isSelectedDragging={isSelectedDragging}
                onMouseOver={(id) => setHoverId(() => id)}
                onMouseOut={() => setHoverId(() => "")}
                hoverId={hoverId}
                analyticsPageName={analyticsPageName}
                {...renameEntryProps}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GridView;
